import { Button, makeStyles, Option, Text } from '@fluentui/react-components';
import { ChevronDownRegular, ChevronRightRegular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useStores } from '../../../common/hooks';
import { CustomerReference } from '../../../models';
import { FlexBox } from '../../shared-components/flex-box';
import { FormInput, SelectOptions } from '../../shared-components/form';
import { PhoneNumberField, PhoneNumberInput } from '../../shared-components/phone-number-input';
import { ValidationMessageList } from '../../shared-components/ValidationMessageList';
import './CustomerReferences.scss';
import { SummaryLayout } from './SummaryLayout';

const useStyles = makeStyles({
  expandableButton: { padding: 0, color: '#000000', justifyContent: 'flex-start' },
  readonlyForInput: { color: '#6d6c6b' },
  readonlyForm: {
    border: '1px solid #F3F2F1',
    backgroundColor: '#F3F2F1',
    ':hover': { border: '1px solid #F3F2F1' },
  },
});

type CustomerReferenceField = keyof Omit<CustomerReference, 'customerReferenceId' | 'nominationId' | 'description'>;

const CustomerReferencesImpl: React.FC = () => {
  const {
    nominationForStaffPageUiStore,
  } = useStores();

  const styles = useStyles();
  const [isCustomerReferenceExpanded, setCustomerReferenceExpanded] = useState(false);

  const [readOnlyPrefix, setReadOnlyPrefix] = useState(!nominationForStaffPageUiStore.isEditingMode);

  const renderFormInput = (key: CustomerReferenceField, customerReference: CustomerReference, customerReferenceIndex: number): React.JSX.Element => {
    const validation = nominationForStaffPageUiStore.customerReferenceValidations[customerReferenceIndex];
    const labels: Record<CustomerReferenceField, string> = {
      prefix: 'Prefix:',
      firstName: 'First name:',
      lastName: 'Last name:',
      title: 'Title / Role:',
      emailAddress: 'Email:',
      phoneNumber: 'Phone number:',
      companyName: 'Company:',
    };

    return (
      <FormInput
        label={labels[key]}
        onChange={(value) => customerReference[key] = value}
        validationMessage={
          validation.fieldErrors[key]?.length
            ? <ValidationMessageList messages={validation.fieldErrors[key]} />
            : undefined
        }
        inputProps={{
          className: nominationForStaffPageUiStore.isEditingMode ? '' : styles.readonlyForm,
          input: { className: nominationForStaffPageUiStore.isEditingMode ? '' : styles.readonlyForInput },
          value: customerReference[key],
          readOnly: !nominationForStaffPageUiStore.isEditingMode,
        }}
      />
    );
  };

  const renderCustomerReference = (customerReference: CustomerReference, index: number): React.JSX.Element => {
    const validation = nominationForStaffPageUiStore.customerReferenceValidations[index];

    return (
      <div
        key={index}
        className='group-form'
      >
        <Text
          as='h3'
          weight='semibold'
          block
        >
          <SummaryLayout>
            Customer Reference #{index + 1}
          </SummaryLayout>
        </Text>
        <SummaryLayout>
          <FlexBox
            direction='column'
            aligns='start'
          >
            <FlexBox isFullWidth className='reference-row'>
              <FlexBox isFullWidth className='reference-row__sub-row'>
                <SelectOptions
                  label='Prefix:'
                  className='field--prefix'
                  validationMessage={
                    validation.fieldErrors.prefix?.length
                      ? <ValidationMessageList messages={validation.fieldErrors.prefix} />
                      : undefined
                  }
                  onOptionSelect={(option) => customerReference.prefix = option.optionValue ?? ''}
                  dropdownProps={{
                    className: nominationForStaffPageUiStore.isEditingMode ? '' : styles.readonlyForm,
                    open: !readOnlyPrefix,
                    onOpenChange: (_, data) => nominationForStaffPageUiStore.isEditingMode ? setReadOnlyPrefix(!data.open) : setReadOnlyPrefix(true),
                    value: customerReference.prefix,
                    'aria-disabled': readOnlyPrefix,
                    selectedOptions: [customerReference.prefix],
                    button: { className: nominationForStaffPageUiStore.isEditingMode ? '' : styles.readonlyForInput }
                  }}
                >
                  <Option text='' />
                  <Option value='Mr.'>Mr.</Option>
                  <Option value='Mrs.'>Mrs.</Option>
                  <Option value='Ms.'>Ms.</Option>
                </SelectOptions>
                {renderFormInput('firstName', customerReference, index)}
              </FlexBox>
              {renderFormInput('lastName', customerReference, index)}
            </FlexBox>
            <FlexBox isFullWidth className='reference-row'>
              {renderFormInput('title', customerReference, index)}
              {renderFormInput('emailAddress', customerReference, index)}
            </FlexBox>
            <FlexBox isFullWidth className='reference-row'>
              <PhoneNumberInput
                id={PhoneNumberField.CustomerReference}
                value={customerReference.phoneNumber}
                onChange={(phoneNumber) => {
                  customerReference.phoneNumber = phoneNumber
                }}
                onCountryChange={(id, newCountryCode) => {
                  nominationForStaffPageUiStore.setSelectedCountryCode(id, newCountryCode);
                }}
                error={
                  validation.fieldErrors.phoneNumber?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.phoneNumber} />
                    : undefined
                }
                label='Phone number:'
                required
                readonly={!nominationForStaffPageUiStore.isEditingMode}
              />
              {renderFormInput('companyName', customerReference, index)}
            </FlexBox>
            <FormInput
              label='Brief description:'
              onChange={(value) => customerReference.description = value}
              validationMessage={
                validation.fieldErrors.description?.length
                  ? <ValidationMessageList messages={validation.fieldErrors.description} />
                  : undefined
              }
              textAreaProps={{
                className: nominationForStaffPageUiStore.isEditingMode ? '' : styles.readonlyForm,
                textarea: { className: nominationForStaffPageUiStore.isEditingMode ? '' : styles.readonlyForInput },
                value: customerReference.description,
                readOnly: !nominationForStaffPageUiStore.isEditingMode,
              }}
            />
          </FlexBox>
        </SummaryLayout>
      </div>
    );
  }

  return (
    <section
      className='customer-references'
    >
      <SummaryLayout
        title={<Button
          appearance='transparent'
          onClick={() => setCustomerReferenceExpanded(!isCustomerReferenceExpanded)}
          className={styles.expandableButton}
          icon={isCustomerReferenceExpanded ? <ChevronDownRegular /> : <ChevronRightRegular />}
          iconPosition='after'
          aria-expanded={!!isCustomerReferenceExpanded}
        >
          Customer reference
        </Button>}
      />
      {
        isCustomerReferenceExpanded &&
        <div
          className='customer-references__group'
        >
          {
            nominationForStaffPageUiStore.nominationForStaff.nomination.customerReferences.map((customerReference, index) => {
              return (
                renderCustomerReference(customerReference, index)
              );
            })
          }
        </div>
      }
    </section>
  );
}

export const CustomerReferences = observer(CustomerReferencesImpl);
